/**
 * Every Firestore path in one place (architecture §8). Callers never assemble path strings.
 */
export const paths = {
    user: (uid) => `users/${uid}`,
    userDevices: (uid) => `users/${uid}/devices`,
    userDevice: (uid, deviceId) => `users/${uid}/devices/${deviceId}`,
    userLinks: (uid) => `users/${uid}/links`,
    savedScopes: (uid) => `users/${uid}/savedScopes`,
    /** Inbox rows for chats and tasks that involve the person (WP-054); written by the api only. */
    activities: (uid) => `users/${uid}/activity`,
    activity: (uid, activityId) => `users/${uid}/activity/${activityId}`,
    workspaces: () => "workspaces",
    workspace: (ws) => `workspaces/${ws}`,
    channels: (ws) => `workspaces/${ws}/channels`,
    channel: (ws, channelId) => `workspaces/${ws}/channels/${channelId}`,
    chatMessages: (ws, channelId) => `workspaces/${ws}/channels/${channelId}/messages`,
    readMarker: (ws, channelId, uid) => `workspaces/${ws}/channels/${channelId}/reads/${uid}`,
    tasks: (ws) => `workspaces/${ws}/tasks`,
    task: (ws, taskId) => `workspaces/${ws}/tasks/${taskId}`,
    /** A task's history, one document per edit (WP-054). */
    taskChanges: (ws, taskId) => `workspaces/${ws}/tasks/${taskId}/changes`,
    links: (ws) => `workspaces/${ws}/links`,
    meetings: (ws) => `workspaces/${ws}/meetings`,
    meeting: (ws, meetingId) => `workspaces/${ws}/meetings/${meetingId}`,
    /** Server-only: the sync worker's follow-up of a meeting until its notes exist (WP-017). */
    meetWatches: () => "meetWatches",
    meetWatch: (meetingId) => `meetWatches/${meetingId}`,
    accounts: () => "accounts",
    account: (accountId) => `accounts/${accountId}`,
    accountSecret: (accountId) => `accountSecrets/${accountId}`,
    /** One-time PKCE verifier for an in-flight connect (server only, §5). */
    oauthState: (nonce) => `oauthStates/${nonce}`,
    threads: (accountId) => `accounts/${accountId}/threads`,
    thread: (accountId, threadId) => `accounts/${accountId}/threads/${threadId}`,
    mailMessages: (accountId) => `accounts/${accountId}/messages`,
    mailMessage: (accountId, messageId) => `accounts/${accountId}/messages/${messageId}`,
    /** Server-only: every header and provider field of a message (WP-061, §6.4). */
    messageRaws: (accountId) => `accounts/${accountId}/messageRaw`,
    messageRaw: (accountId, messageId) => `accounts/${accountId}/messageRaw/${messageId}`,
    /** Sync only: addresses the owner has sent to, by `sha256(lowercased address)` (WP-061). */
    correspondents: (accountId) => `accounts/${accountId}/correspondents`,
    correspondent: (accountId, hash) => `accounts/${accountId}/correspondents/${hash}`,
    tags: (accountId) => `accounts/${accountId}/tags`,
    tag: (accountId, tagId) => `accounts/${accountId}/tags/${tagId}`,
    /** Sender overrides (WP-063): the owner reads, only the api and sync write. */
    senderKinds: () => "senderKinds",
    senderKind: (id) => `senderKinds/${id}`,
    /** Server-only (WP-063): Claude was asked about a sender once, and the per-owner daily count. */
    senderGuess: (id) => `senderGuesses/${id}`,
    senderGuessDay: (ownerUid, day) => `senderGuessDays/${ownerUid}_${day}`,
    snoozes: () => "snoozes",
    snooze: (id) => `snoozes/${id}`,
    rules: () => "rules",
    operations: () => "operations",
    operation: (requestId) => `operations/${requestId}`,
    audit: () => "audit",
    approvals: () => "approvals",
};
/** Collection-group id of the unified inbox query. */
export const THREADS_GROUP = "threads";
/**
 * Document id of a cached message or thread: the provider id itself (`accounts/{a}/messages/
 * {providerId}`, threads by provider thread id), which the api's mail actions rely on. Gmail ids
 * are hex and Graph immutable ids URL-safe base64; an id Firestore cannot hold is an error.
 */
export function cacheDocId(providerId) {
    if (providerId.length === 0)
        throw new Error("provider id is empty");
    if (providerId.includes("/") || providerId === "." || providerId === ".." || /^__.*__$/.test(providerId)) {
        throw new Error(`provider id ${providerId} cannot be a document id`);
    }
    return providerId;
}
/** Document id of a tag: its lower-cased name, percent-encoded (Gmail nests labels with `/`). */
export function tagDocId(name) {
    if (name.length === 0)
        throw new Error("tag name is empty");
    return encodeURIComponent(name.toLowerCase());
}
//# sourceMappingURL=paths.js.map